import type Quill from 'quill/core';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import type { SetNonNullable } from 'type-fest';
import type { AnnotationVersionChange } from './AnnotationVersionChangeCard';
import AnnotationVersionChangeCard from './AnnotationVersionChangeCard';
import type { EditVersionChange } from './EditVersionChangeCard';
import EditVersionChangeCard from './EditVersionChangeCard';
import type { ThreadVersionChange } from './ThreadVersionChangeCard';
import ThreadVersionChangeCard from './ThreadVersionChangeCard';
import { useVersionChangesContext } from './VersionChanges';
import type {
  MagneticWallFocus,
  MagneticWallHighlight,
} from '../../editor/editorSlice';
import { getClosestAttribute } from '../../editor/formats/utils/attributors';
import VersionChange from '../../editor/formats/VersionChange';
import { useQuill } from '../../editor/QuillContext';
import { getHeaderCollapse } from '../../editor/useHeaderCollapse';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import annotationThreadHash from '../../utils/url/annotationThreadHash';
import commentThreadHash from '../../utils/url/commentThreadHash';
import { getMagneticWallItemContentRects } from '../MagneticWall';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import type { RelativeContentRects } from '../MagneticWallLayout';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import useMagneticWallItemSize from '../useMagneticWallItemSize';
import useVisibleMagneticWallItems from '../useVisibleMagneticWallItems';
import getCollapsedHeaderRect from '../utils/getCollapsedHeaderRect';
import parseMagneticWallHash from '../utils/parseMagneticWallHash';
import toRelativeDOMRect from '../utils/toRelativeDOMRect';

const THREAD_FRAGMENT = gql(`
  fragment NewPostHistoryMagneticWall_thread on CommentThread {
    id
    ...ThreadVersionChangeCard_thread
  }
`);

const ANNOTATION_FRAGMENT = gql(`
  fragment NewPostHistoryMagneticWall_annotation on Annotation {
    id
    ...AnnotationVersionChangeCard_annotation
  }
`);

type NewVersionChange =
  | EditVersionChange
  | ThreadVersionChange
  | AnnotationVersionChange;

interface InnerNewPostHistoryMagneticWallProps {
  versionChanges: NewVersionChange[];
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  historyInsertedAt: Date;
  showAnnotationNewBadge: boolean;
  magneticWallItemSize: MagneticWallItemSize;
  canEdit: boolean;
}

const InnerNewPostHistoryMagneticWall = ({
  versionChanges,
  historyInsertedAt,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  showAnnotationNewBadge,
  magneticWallItemSize,
  canEdit,
}: InnerNewPostHistoryMagneticWallProps) => {
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();
  const { focusedId, focusById } = useVersionChangesContext();

  const visibleItems = useMemo(
    () =>
      versionChanges
        .map((item) => ({
          ...item,
          top: layout.tops[item.id],
          isOverlapped: layout.overlappedIds.has(item.id),
        }))
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges],
  );

  const hasVisibleItems = visibleItems.length > 0;
  const location = useLocation();

  useEffect(() => {
    if (!hasVisibleItems) return;

    const parseResult = parseMagneticWallHash(location.hash);
    if (!parseResult) return;

    setMagneticWallFocus({
      active: true,
      id: parseResult.id,
      type: parseResult.type,
      hasExpandIntent: true,
    });
  }, [hasVisibleItems, location.hash, setMagneticWallFocus]);

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();
  }, [visibleItems, debouncedUpdateDesiredPositions, updateThreadHeights]);

  return (
    <ClickOutsideHandler
      onClickOutside={(e) => {
        const { target } = e;

        if (
          target instanceof Element &&
          // clicking on a comment / annotation within the post
          // shouldn't blur the magneticWallFocus, as it's selecting it
          getClosestAttribute(target) != null
        ) {
          return;
        }

        setMagneticWallFocus(null);
      }}
    >
      {visibleItems.map((versionChange) => {
        const copyLink = (hash: string) => {
          const link = new URL(window.location.href);
          link.hash = hash;
          navigator.clipboard.writeText(link.toString());
        };
        if (versionChange.type === 'thread') {
          const isFocused = versionChange.id === magneticWallFocus?.id;
          return (
            <MagneticWallLayout.Item
              id={versionChange.id}
              focused={isFocused}
              top={versionChange.top}
              key={versionChange.id}
            >
              <ThreadVersionChangeCard
                focused={isFocused}
                versionChange={versionChange}
                historyInsertedAt={historyInsertedAt}
                canEdit={canEdit}
                onFocus={() =>
                  setMagneticWallFocus({
                    id: versionChange.id,
                    type: 'comment',
                    active: true,
                    hasExpandIntent: false,
                  })
                }
                onCopyLink={() => copyLink(commentThreadHash(versionChange.id))}
                onRemoveHighlight={() => setMagneticWallHighlight(null)}
                onHighlight={() =>
                  setMagneticWallHighlight({
                    id: versionChange.id,
                    type: 'comment',
                  })
                }
                magneticWallItemSize={magneticWallItemSize}
              />
            </MagneticWallLayout.Item>
          );
        }

        if (versionChange.type === 'annotation') {
          const isFocused = versionChange.id === magneticWallFocus?.id;

          return (
            <MagneticWallLayout.Item
              id={versionChange.id}
              focused={isFocused}
              top={versionChange.top}
              key={versionChange.id}
            >
              <AnnotationVersionChangeCard
                focused={isFocused}
                versionChange={versionChange}
                onFocus={() =>
                  setMagneticWallFocus({
                    id: versionChange.id,
                    type: 'annotation',
                    active: true,
                    hasExpandIntent: false,
                  })
                }
                onCopyLink={() =>
                  copyLink(annotationThreadHash(versionChange.id))
                }
                onRemoveHighlight={() => setMagneticWallHighlight(null)}
                onHighlight={() =>
                  setMagneticWallHighlight({
                    id: versionChange.id,
                    type: 'annotation',
                  })
                }
                showAnnotationNewBadge={showAnnotationNewBadge}
                magneticWallItemSize={magneticWallItemSize}
              />
            </MagneticWallLayout.Item>
          );
        }

        const isFocused = versionChange.id === focusedId;

        return (
          <MagneticWallLayout.Item
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            key={versionChange.id}
          >
            <EditVersionChangeCard
              versionChange={versionChange}
              focused={isFocused}
              onClick={() => {
                focusById(versionChange.id);
              }}
              historyInsertedAt={historyInsertedAt}
              magneticWallItemSize={magneticWallItemSize}
            />
          </MagneticWallLayout.Item>
        );
      })}
    </ClickOutsideHandler>
  );
};

function isInlineElement(el: Element) {
  const d = getComputedStyle(el).display;
  return d === 'inline' || d.startsWith('inline-');
}

function getVersionChangeContentRects(quill: Quill): RelativeContentRects[] {
  const containerRect = quill.container.getBoundingClientRect();
  const collapsedHeaders =
    getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];

  return Array.from(
    quill.container.querySelectorAll<HTMLElement>(`[${VersionChange.keyName}]`),
  ).flatMap((element) => {
    const id = VersionChange.value(element) as string;

    const collapsedHeaderRect = getCollapsedHeaderRect(
      element,
      collapsedHeaders,
    );
    if (collapsedHeaderRect) {
      return [
        {
          rect: toRelativeDOMRect(collapsedHeaderRect, containerRect),
          id,
        },
      ];
    }

    const contentRect = element.getClientRects()[0];
    if (!contentRect) return [];

    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);

    const finalContentRect = (() => {
      // If the element is a paragraph, then it means the diff is a line break
      // and we show this by showing a rect at the end of the line.
      if (element instanceof HTMLParagraphElement) {
        return DOMRect.fromRect({
          x: contentRect.x + contentRect.width,
          y: contentRect.y + contentRect.height - lineHeight,
          width: 12,
          height: lineHeight,
        });
      }

      if (isInlineElement(element)) {
        // Line break diffs are 26px high, inline diffs are 24px high.
        // This means both could have different top values, but still be on the same line.
        // This can cause the ordering of the items in the magnetic wall layout to be incorrect.
        // In order to fix this, we normalize all content rects to top their line.
        return DOMRect.fromRect({
          ...contentRect,
          y: contentRect.y + contentRect.height / 2 - lineHeight / 2,
          height: lineHeight,
        });
      }

      return contentRect;
    })();

    return [
      {
        rect: toRelativeDOMRect(finalContentRect, containerRect),
        id,
      },
    ];
  });
}

interface NewPostHistoryMagneticWallProps {
  threads: DocumentType<typeof THREAD_FRAGMENT>[];
  annotations: DocumentType<typeof ANNOTATION_FRAGMENT>[];
  historyInsertedAt: Date;
  setMagneticWallFocus: (magneticWallFocus: MagneticWallFocus | null) => void;
  magneticWallFocus: MagneticWallFocus | null;
  setMagneticWallHighlight: (
    magneticWallHighlight: MagneticWallHighlight | null,
  ) => void;
  magneticWallHighlight: MagneticWallHighlight | null;
  showAnnotationNewBadge: boolean;
  canEdit: boolean;
}

const NewPostHistoryMagneticWall = ({
  historyInsertedAt,
  threads,
  annotations,
  setMagneticWallFocus,
  magneticWallFocus,
  setMagneticWallHighlight,
  magneticWallHighlight,
  showAnnotationNewBadge,
  canEdit,
}: NewPostHistoryMagneticWallProps) => {
  const { focusedId, versionChanges: editVersionChanges } =
    useVersionChangesContext();

  useVisibleMagneticWallItems([
    ...annotations.map((annotation) => ({
      id: annotation.id,
      type: 'annotation' as const,
    })),
    ...threads
      .filter((thread) => {
        return (
          thread.resolvedAt == null ||
          // If the thread is resolved, we only show focus & highlight
          magneticWallFocus?.id === thread.id ||
          magneticWallHighlight?.id === thread.id
        );
      })
      .map((thread) => ({ id: thread.id, type: 'comment' as const })),
  ]);

  const versionChanges: NewVersionChange[] = useMemo(
    () => [
      ...threads.map((thread) => ({
        id: thread.id,
        type: 'thread' as const,
        thread,
      })),
      ...annotations.map((annotation) => ({
        id: annotation.id,
        type: 'annotation' as const,
        annotation,
      })),
      ...editVersionChanges.map((change) => ({
        ...change,
        type: 'edit' as const,
      })),
    ],
    [editVersionChanges, threads, annotations],
  );

  const { quill } = useQuill();
  const containerRef = useRef<HTMLDivElement>(null);
  const magneticWallItemSize = useMagneticWallItemSize(containerRef);

  const focus = useMemo(() => {
    if (magneticWallFocus) return magneticWallFocus;

    return focusedId
      ? {
          id: focusedId,
          active: true,
        }
      : null;
  }, [magneticWallFocus, focusedId]);

  return (
    <MagneticWallLayout.Root
      containerRef={containerRef}
      itemSize={magneticWallItemSize}
      magneticWallItems={versionChanges}
      focus={focus}
      getRelativeContentRects={() =>
        quill
          ? [
              ...getMagneticWallItemContentRects(quill),
              ...getVersionChangeContentRects(quill),
            ]
          : []
      }
    >
      <InnerNewPostHistoryMagneticWall
        versionChanges={versionChanges}
        setMagneticWallFocus={setMagneticWallFocus}
        magneticWallFocus={magneticWallFocus}
        setMagneticWallHighlight={setMagneticWallHighlight}
        historyInsertedAt={historyInsertedAt}
        showAnnotationNewBadge={showAnnotationNewBadge}
        magneticWallItemSize={magneticWallItemSize}
        canEdit={canEdit}
      />
    </MagneticWallLayout.Root>
  );
};

export default NewPostHistoryMagneticWall;
